import React, { ReactElement, useCallback } from "react";
import {
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    DrawerFooter,
} from "src/components/Drawer";
import { QualityHubTaskDetails } from "../QualityHubTaskDetails";
import { QualityHubTaskDetailsDrawerProps, QualityHubTaskDetailsDrawerStateProps } from "./QualityHubTaskDetailsDrawer.types";
import { Checkbox } from "src/components/Checkbox";
import { LargeBody } from "src/components/Text";
import { UnstyledButton } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { useSelector, useDispatch } from "react-redux";
import { SpintrTypes } from "src/typings";
import { deleteTaskStatus, createTaskStatus } from "src/redux";
import { Conditional } from "src/components/Conditional";
import { PrimaryButton } from "@fluentui/react";
import { localize } from "src/l10n";

/**
 * Renders the QualityHubTaskDetailsDrawer component.
 *
 * @param {QualityHubTaskDetailsDrawerProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTaskDetailsDrawer({
    onCreateActionClick,
    onEditClicked,
    onDismiss,
    open,
    task,
}: QualityHubTaskDetailsDrawerProps): ReactElement | null {
    const dispatch = useDispatch();
    const {
        status,
        smallViewMode,
        taskList,
        isHubAdministrator,
    } = useSelector<Spintr.AppState, QualityHubTaskDetailsDrawerStateProps>(
        function getStateProps(appState) {
            const isAdministrator = appState.profile.active.roles.includes("administrators");
            const isHubAdministrator = isAdministrator || (appState.privileges.data || [])
                .some((p: any) => p.value && p.privilegeId === SpintrTypes.Privilege.IsQualityHubAdministrator);

            return {
                status: appState.qualityHub.listsById[task?.listId || 0]?.statuses?.find(
                    (status) => status.itemId === task?.id,
                ),
                smallViewMode: appState.ui.isSmallViewMode,
                taskList: appState.qualityHub.listsById[task?.listId || 0],
                isAdministrator,
                isHubAdministrator,
            };
        }
    );

    const canEdit = taskList?.listType === SpintrTypes.TaskListType.ActionList;
    const canCreateAction =
        taskList?.listType !== SpintrTypes.TaskListType.ActionList &&
        task?.itemType !== SpintrTypes.QualityHubItemType.Reminder;

    const headerActions = (
        <div className="QualityHubTaskDetailsDrawer-headerActions">
            <Conditional condition={canEdit}>
                <UnstyledButton className="QualityHubTaskDetailsDrawer-editButton"
                                onClick={onEditClicked}
                                onClickData={task}>
                    <Visage2Icon icon="edit"
                                 size="big" />
                </UnstyledButton>
            </Conditional>
            <UnstyledButton className="QualityHubTaskDetailsDrawer-closeButton"
                            onClick={onDismiss}>
                <Visage2Icon icon="close-circle"
                             size="big" />
            </UnstyledButton>
        </div>
    );

    const onOpenChange = useCallback(
        (isOpen: boolean) => {
            if (!isOpen) {
                onDismiss?.();
            }
        },
        [onDismiss]);

    return (
        <Drawer className="QualityHubTaskDetailsDrawer"
                onOpenChange={onOpenChange}
                open={open}
                position="end"
                size={smallViewMode ? "full" : "medium"}>
            <DrawerHeader>
                <DrawerHeaderTitle action={headerActions}>
                    <div className="QualityHubTaskDetailsDrawer-title">
                        <LargeBody color="contentDark"
                                   weight="semiBold">
                            {task?.title}
                        </LargeBody>
                    </div>
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <QualityHubTaskDetails canCreateAction={canCreateAction}
                                       onCreateActionClick={onCreateActionClick}
                                       task={task} />
            </DrawerBody>
            <DrawerFooter className="QualityHubTaskDetailsDrawer-footer">
                <PrimaryButton onClick={onDismiss}>
                    {localize("Stang")}
                </PrimaryButton>
            </DrawerFooter>
        </Drawer>
    );
}

export default QualityHubTaskDetailsDrawer;

